import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { fromQueryString, toQueryString } from '@shared/api/helpers/queryString';
import { Data, PaginatedResourceResponse, PartialQuery, Query } from '@shared/api/types';
import { randomString } from '@shared/utils/helpers';

type UseDataOptions<T> = {
  key?: string;
  get: (query: PartialQuery) => Promise<PaginatedResourceResponse<T>>;
  query?: PartialQuery;
  defaults?: PartialQuery;
  history?: boolean;
};

export function useData<T>({
  key,
  get,
  query: fixedQuery = {},
  defaults: defaultQuery,
  history: withHistory = true,
}: UseDataOptions<T>): Data<T> {
  const queryClient = useQueryClient();

  const queryKey = useMemo(() => key ?? randomString(), [key]);

  const [query, onQueryChange] = useState<Query>(() => {
    const query = fromQueryString(withHistory ? window.location.search.substring(1) : '', defaultQuery);

    return {
      sort: fixedQuery.sort ?? query.sort,
      filter: {
        ...fixedQuery.filter,
        ...query.filter,
      },
      include: [...(fixedQuery.include ?? []), ...query.include],
      page: {
        number: fixedQuery.page?.number ?? query.page.number,
        size: fixedQuery.page?.size ?? query.page.size,
      },
    };
  });

  const queryString = toQueryString(query);

  const useQueryResult = useQuery([queryKey, queryString], () => get(query), { keepPreviousData: true });

  useEffect(() => {
    if (!withHistory) {
      return;
    }

    const { include, ...historyQuery } = query;

    const historyQueryString = toQueryString(historyQuery, {
      sort: fixedQuery?.sort ?? defaultQuery?.sort,
      filter: {
        ...fixedQuery?.filter,
        ...defaultQuery?.filter,
      },
      page: {
        number: fixedQuery?.page?.number ?? defaultQuery?.page?.number,
        size: fixedQuery?.page?.size ?? defaultQuery?.page?.size,
      },
    });

    window.history.replaceState(
      {},
      '',
      historyQueryString ? `${window.location.pathname}?${historyQueryString}` : window.location.pathname
    );
  }, [withHistory, queryString]);

  return {
    data: useQueryResult.data?.data,
    pages: useQueryResult.data?.meta.last_page ?? 1,
    results: useQueryResult.data?.meta.total ?? 0,
    isLoading: useQueryResult.isLoading,
    refresh: () => queryClient.invalidateQueries(queryKey),
    query,
    onQueryChange,
  };
}
